/**
 * Statute fetch policy — V104-G-2 / ADR-0076 §4 (D-104-10). Pure functions.
 *
 * The URL comes from an editor-approved `legislation` row, but the fetch runs
 * on our server, so every URL (the first one and every redirect hop) passes
 * `checkStatuteUrl` before it is requested:
 *
 *   - scheme `https:` only; no userinfo (`user:pass@`) in the URL;
 *   - host must be a DNS name: no IPv4 or IPv6 literal in any spelling
 *     (dotted, decimal, hex, octal, shortened), no `localhost`, no
 *     `*.localhost` / `*.local` / `*.internal`, and no single-label host;
 *   - no explicit port other than 443 (ADR-0076 is silent; the safest choice);
 *   - not our own hosts: gacpro.io, *.gacpro.io, *.workers.dev (V104-G-9).
 *
 * Also here: the HTML → text extraction, the grounding normalization
 * (ADR-0076 §6) and the source-URL normalization the coverage check uses
 * (ADR-0076 §5). Nothing in this file performs I/O.
 */
/** ADR-0076 §4 limits. */
export const STATUTE_FETCH_LIMITS = {
    timeoutMs: 15_000,
    maxBytes: 5 * 1024 * 1024,
    maxRedirects: 3,
    minTextChars: 2_000,
    /** One retry on a network error, a timeout or a 5xx; none on a 4xx. */
    maxAttempts: 2,
};
/** Content types the fetcher accepts (ADR-0076 §4). Anything else, PDF included, is `thin_source`. */
export const STATUTE_CONTENT_TYPES = ['text/html', 'application/xhtml+xml', 'text/plain'];
const BLOCKED_SUFFIXES = ['.localhost', '.local', '.internal'];
/**
 * V104-G-9 (owner default): our own hosts. The server never fetches its own
 * product domain or any Workers subdomain, so a statute URL can't be used to
 * reach our own (or another tenant's) Worker from inside the platform.
 */
const OWN_HOSTS = ['gacpro.io'];
const OWN_SUFFIXES = ['.gacpro.io', '.workers.dev'];
/**
 * True when a hostname could be read as an IPv4 address by some parser:
 * every dot-separated label is decimal, `0x` hex or octal digits. WHATWG URL
 * already canonicalizes `2130706433`, `0x7f.1` and `0177.0.0.1` to
 * `127.0.0.1`; this catches the same spellings if a runtime does not.
 */
function looksLikeIpv4(host) {
    const labels = host.split('.');
    return labels.every((l) => /^(0x[0-9a-f]*|[0-9]+)$/i.test(l));
}
export function checkStatuteUrl(raw, base) {
    let url;
    try {
        url = typeof raw === 'string' ? new URL(raw, base) : new URL(raw.href);
    }
    catch {
        return { ok: false, reason: 'not a valid URL' };
    }
    if (url.protocol !== 'https:')
        return { ok: false, reason: `scheme ${url.protocol} is not allowed (https only)` };
    if (url.username || url.password)
        return { ok: false, reason: 'credentials in the URL are not allowed' };
    if (url.port && url.port !== '443')
        return { ok: false, reason: `port ${url.port} is not allowed` };
    let host = url.hostname.toLowerCase();
    if (host.startsWith('[') || host.includes(':'))
        return { ok: false, reason: 'IP address hosts are not allowed' };
    while (host.endsWith('.'))
        host = host.slice(0, -1);
    if (!host)
        return { ok: false, reason: 'missing host' };
    if (looksLikeIpv4(host))
        return { ok: false, reason: 'IP address hosts are not allowed' };
    if (OWN_HOSTS.includes(host) || OWN_SUFFIXES.some((s) => host.endsWith(s)) || host === 'workers.dev') {
        return { ok: false, reason: `host ${host} is one of our own and is not fetched` };
    }
    if (host === 'localhost' || BLOCKED_SUFFIXES.some((s) => host.endsWith(s))) {
        return { ok: false, reason: `host ${host} is not allowed` };
    }
    const labels = host.split('.');
    if (labels.length < 2)
        return { ok: false, reason: 'host must be a fully qualified DNS name' };
    // A top-level label is never all digits; a numeric last label is an address in disguise.
    if (/^[0-9]+$/.test(labels[labels.length - 1]))
        return { ok: false, reason: 'IP address hosts are not allowed' };
    if (!labels.every((l) => /^[a-z0-9_]([a-z0-9_-]*[a-z0-9_])?$/.test(l) || /^xn--[a-z0-9-]+$/.test(l))) {
        return { ok: false, reason: 'host is not a DNS name' };
    }
    url.hash = '';
    return { ok: true, url };
}
/** The media type of a Content-Type header, lowercased, without parameters. */
export function mediaTypeOf(contentType) {
    return (contentType ?? '').split(';')[0].trim().toLowerCase();
}
export function isAllowedStatuteContentType(contentType) {
    return STATUTE_CONTENT_TYPES.includes(mediaTypeOf(contentType));
}
/** The charset parameter of a Content-Type header, or null. */
export function charsetOf(contentType) {
    const m = /;\s*charset\s*=\s*"?([^";\s]+)"?/i.exec(contentType ?? '');
    return m ? m[1].toLowerCase() : null;
}
// ---- HTML → text -----------------------------------------------------------
/** Longest run of characters read inside one tag (bounds regex work on hostile input). */
const TAG_ATTRS = '[^<>]{0,4096}';
/** Nested dropped elements unwrapped at most this deep. */
const MAX_NESTING_PASSES = 16;
/** Elements dropped with their content (ADR-0076 §4). */
const DROPPED_ELEMENTS = ['script', 'style', 'nav', 'header', 'footer'];
/** Inline elements whose tags vanish without a space (so `<b>Sec</b>tion` stays one word). */
const INLINE_TAGS = new Set(['a', 'abbr', 'b', 'bdi', 'bdo', 'cite', 'code', 'data', 'dfn', 'em', 'i', 'kbd', 'mark', 'q', 's', 'samp', 'small', 'span', 'strong', 'sub', 'sup', 'time', 'u', 'var', 'wbr', 'font']);
const NAMED_ENTITIES = {
    amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', sect: '\u00a7', para: '\u00b6',
    ndash: '\u2013', mdash: '\u2014', lsquo: '\u2018', rsquo: '\u2019', ldquo: '\u201c', rdquo: '\u201d', hellip: '\u2026',
    copy: '\u00a9', reg: '\u00ae', trade: '\u2122', middot: '\u00b7', bull: '\u2022',
};
function decodeEntities(s) {
    return s.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi, (m, body) => {
        if (body[0] === '#') {
            const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
            if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff))
                return ' ';
            return String.fromCodePoint(code);
        }
        return NAMED_ENTITIES[body.toLowerCase()] ?? m;
    });
}
/**
 * HTML → plain text: drop comments and the `script`, `style`, `nav`, `header`
 * and `footer` elements with their content, strip the remaining tags, decode
 * entities, then collapse whitespace. An unclosed dropped element is dropped
 * to the end of the document (fail closed: no script text leaks into a draft).
 */
export function htmlToText(html) {
    // Quantifiers inside tags are bounded (TAG_ATTRS) so a hostile page of unclosed
    // `<a<a<a…` cannot make a pass quadratic; nesting passes are capped, after which
    // the unclosed-element rule drops from the first remaining opener to the end.
    let s = html.replace(/<!--[\s\S]*?(-->|$)/g, ' ');
    s = s.replace(/<!\[CDATA\[[\s\S]*?(\]\]>|$)/g, ' ');
    for (const el of DROPPED_ELEMENTS) {
        // Innermost pairs first (no nested opener inside the match), repeated so
        // `<nav><nav>…</nav>…</nav>` leaves nothing behind.
        const paired = new RegExp(`<${el}\\b${TAG_ATTRS}>(?:(?!<${el}\\b)[\\s\\S])*?<\\/${el}\\s*>`, 'gi');
        for (let pass = 0; pass < MAX_NESTING_PASSES; pass++) {
            const next = s.replace(paired, ' ');
            if (next === s)
                break;
            s = next;
        }
        s = s.replace(new RegExp(`<${el}\\b${TAG_ATTRS}>[\\s\\S]*$`, 'i'), ' ');
        s = s.replace(new RegExp(`<\\/${el}\\s*>`, 'gi'), ' ');
    }
    s = s.replace(new RegExp(`<\\/?([a-z][a-z0-9-]{0,63})\\b${TAG_ATTRS}>`, 'gi'), (_m, tag) => (INLINE_TAGS.has(tag.toLowerCase()) ? '' : ' '));
    s = s.replace(new RegExp(`<[!?]${TAG_ATTRS}>`, 'g'), ' ');
    s = decodeEntities(s);
    return collapseWhitespace(s);
}
export function collapseWhitespace(s) {
    return s.replace(/[\s\u00a0\u2000-\u200b\u2028\u2029\u3000\ufeff]+/g, ' ').trim();
}
/** Plain text → normalized text (whitespace collapsed), for `text/plain` responses and editor input. */
export function plainTextToText(text) {
    return collapseWhitespace(text.replace(/\u0000/g, ' '));
}
// ---- Grounding normalization (ADR-0076 §6) ----------------------------------
const QUOTE_MAP = {
    '\u2018': "'", '\u2019': "'", '\u201a': "'", '\u201b': "'", '\u2032': "'", '\u00b4': "'",
    '\u201c': '"', '\u201d': '"', '\u201e': '"', '\u201f': '"', '\u2033': '"', '\u00ab': '"', '\u00bb': '"',
    '\u2010': '-', '\u2011': '-', '\u2012': '-', '\u2013': '-', '\u2014': '-', '\u2015': '-', '\u2212': '-', '\ufe58': '-', '\ufe63': '-', '\uff0d': '-',
};
/**
 * ADR-0076 §6: Unicode NFKC, lowercase, curly quotes and dashes mapped to
 * ASCII, every character except letters, digits and spaces removed,
 * whitespace collapsed. Applied identically to the statute text and to every
 * excerpt.
 */
export function normalizeForGrounding(text) {
    let s = text.normalize('NFKC').toLowerCase();
    s = s.replace(/[\u2018\u2019\u201a\u201b\u2032\u00b4\u201c\u201d\u201e\u201f\u2033\u00ab\u00bb\u2010-\u2015\u2212\ufe58\ufe63\uff0d]/g, (c) => QUOTE_MAP[c] ?? c);
    s = s.replace(/\s+/gu, ' ');
    s = s.replace(/[^\p{L}\p{N} ]/gu, '');
    return s.replace(/ +/g, ' ').trim();
}
/** Minimum normalized excerpt length (ADR-0076 §6). */
export const MIN_EXCERPT_CHARS = 40;
// ---- Source URL normalization (ADR-0076 §5) ---------------------------------
/**
 * Normalize a source URL for the coverage comparison: the scheme dropped
 * (http and https name the same document), host lowercased with a leading
 * `www.` dropped, default ports and the fragment removed, a trailing slash on
 * the path removed; the query is kept. Returns null for anything that does
 * not parse as an http(s) URL.
 */
export function normalizeSourceUrl(raw) {
    if (!raw || typeof raw !== 'string')
        return null;
    let url;
    try {
        url = new URL(raw.trim());
    }
    catch {
        return null;
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:')
        return null;
    const host = url.hostname.toLowerCase().replace(/\.$/, '').replace(/^www\./, '');
    const port = url.port && !((url.protocol === 'https:' && url.port === '443') || (url.protocol === 'http:' && url.port === '80')) ? `:${url.port}` : '';
    const path = url.pathname.replace(/\/+$/, '');
    return `//${host}${port}${path}${url.search}`;
}
//# sourceMappingURL=statute-fetch-policy.js.map